import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Globe, Search, Power, SlidersHorizontal, PauseCircle, PlayCircle, Clock, Zap } from "lucide-react";
import AgenciaPlusWizardDialog from "@/components/admin/AgenciaPlusWizardDialog";
import EntityConfigDialog from "@/components/admin/EntityConfigDialog";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth-context";

const toNum = (v) => (v === "" || v == null ? 0 : Number(v));
const EMPTY = { name: "", code: "", grupo_id: "", commission_pct: 0, currency: "BS", currencies: ["BS"], address: "", phone: "", rif: "", email: "", active: true, sales_limit_bs: 0, enable_web_access: false };
const WEB_ACCESS_TEMP_PASSWORD = "Connect2026*";
const CURRENCY_LABELS = { BS: "Bolívares (Bs)", USD: "Dólares (USD)", COP: "Pesos (COP)" };

const fmtLimit = (v) => (!v || Number(v) <= 0) ? "Ilimitado" : `${Number(v).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Bs`;

const StatusBadge = ({ e, testid }) => {
  const [label, cls] = !e.active ? ["Inactivo", "border-rose-500/40 text-rose-500 dark:text-rose-300 bg-rose-500/10"]
    : e.suspended ? ["Suspendido", "border-amber-500/40 text-amber-600 dark:text-amber-300 bg-amber-500/10"]
      : ["Activo", "border-emerald-500/40 text-emerald-600 dark:text-emerald-300 bg-emerald-500/10"];
  return <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>{label}</span>;
};

export default function AgenciasPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cloneFor, setCloneFor] = useState(null);
  const [cloneForm, setCloneForm] = useState({ target_name: "", target_code: "", grupo_id: "" });
  const [search, setSearch] = useState("");
  const [toggleTarget, setToggleTarget] = useState(null);
  const [configFor, setConfigFor] = useState(null);
  const [plusOpen, setPlusOpen] = useState(false);

  const { data: grupos = [] } = useQuery({ queryKey: ["grupos"], queryFn: async () => (await api.get("/grupos")).data });
  const { data: agencias = [], isLoading } = useQuery({ queryKey: ["agencias"], queryFn: async () => (await api.get("/agencias")).data });
  const grupoMap = Object.fromEntries(grupos.map((g) => [g.id, g]));
  const { user } = useAuth();
  const isBanca = user?.role === "banca_owner" || user?.role === "admin";
  const grantConsult = useMutation({
    mutationFn: async (id) => (await api.patch(`/agencias/${id}/grant-consult`)).data,
    onSuccess: (a) => { toast.success("Ventana de consulta de 15 min otorgada"); qc.invalidateQueries({ queryKey: ["agencias"] }); setEditing((prev) => ({ ...prev, ...a })); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  const save = useMutation({
    mutationFn: async (raw) => {
      const payload = { ...raw, commission_pct: toNum(raw.commission_pct), sales_limit_bs: toNum(raw.sales_limit_bs) };
      if (editing) return (await api.patch(`/agencias/${editing.id}`, payload)).data;
      return (await api.post("/agencias", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Agencia actualizada" : "Agencia creada");
      qc.invalidateQueries({ queryKey: ["agencias"] });
      setOpen(false); setEditing(null); setForm(EMPTY);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/agencias/${id}`)).data,
    onSuccess: () => { toast.success("Agencia eliminada"); qc.invalidateQueries({ queryKey: ["agencias"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al eliminar"),
  });
  const clone = useMutation({
    mutationFn: async ({ id, payload }) => (await api.post(`/agencias/${id}/clone-config`, payload)).data,
    onSuccess: () => { toast.success("Agencia clonada"); qc.invalidateQueries({ queryKey: ["agencias"] }); setCloneFor(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al clonar"),
  });
  const suspend = useMutation({
    mutationFn: async ({ id, suspended }) => (await api.patch(`/agencias/${id}/suspension`, { suspended })).data,
    onSuccess: (r) => { toast.success(`Agencia ${r.suspended ? "suspendida" : "reanudada"}`); qc.invalidateQueries({ queryKey: ["agencias"] }); qc.invalidateQueries({ queryKey: ["grupos"] }); qc.invalidateQueries({ queryKey: ["taquillas"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const toggleStatus = useMutation({
    mutationFn: async (id) => (await api.patch(`/agencias/${id}/toggle-status`)).data,
    onSuccess: (a) => { toast.success(`Agencia ${a.active ? "activada" : "inactivada"}`); qc.invalidateQueries({ queryKey: ["agencias"] }); setToggleTarget(null); },
    onError: (e) => { toast.error(e?.response?.data?.detail || "Error"); setToggleTarget(null); },
  });

  const filtered = agencias.filter((a) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (a.name || "").toLowerCase().includes(q) || (a.code || "").toLowerCase().includes(q);
  });

  const buildPayload = (f) => {
    // Solo enviamos campos soportados por el backend (rif/email son informativos por ahora)
    const { rif, email, ...rest } = f;
    return rest;
  };
  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, grupo_id: "" }); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, code: a.code, grupo_id: a.grupo_id, commission_pct: a.commission_pct, currency: a.currency || "BS", currencies: a.currencies?.length ? a.currencies : [a.currency || "BS"], address: a.address || "", phone: a.phone || "", rif: "", email: "", active: a.active, sales_limit_bs: a.sales_limit_bs || 0, enable_web_access: false });
    setOpen(true);
  };
  const openClone = (a) => { setCloneFor(a); setCloneForm({ target_name: `${a.name} (copia)`, target_code: "", grupo_id: a.grupo_id }); };

  return (
    <div className="space-y-4" data-testid="admin-table-agencias">
      <AgenciaPlusWizardDialog open={plusOpen} onOpenChange={setPlusOpen} grupos={grupos} agencias={agencias} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Agencias</h1>
          <p className="text-sm text-muted-foreground">Banca → Grupo → <span className="text-primary font-medium">Agencia</span> → Taquilla.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={openCreate} disabled={grupos.length === 0} data-testid="agencia-create-button"><Plus size={16} className="mr-1.5"/>Nueva Agencia</Button>
          <Button onClick={() => setPlusOpen(true)} disabled={grupos.length === 0} data-testid="agencia-plus-open-button" className="shadow-[0_0_0_3px_hsl(var(--primary)/0.18)]"><Zap size={16} className="mr-1.5"/>Agencia Plus</Button>
        </div>
      </div>
      {grupos.length === 0 && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300 px-4 py-3 text-sm">Primero crea un Grupo para poder registrar agencias.</div>}

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o código…" className="pl-9" data-testid="agencia-search-input"/>
      </div>

      <Card className="fintech-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm fintech-table">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Grupo</th>
                <th className="px-4 py-3">Teléfono</th>
                <th className="px-4 py-3">Moneda</th>
                <th className="px-4 py-3 text-right">Comisión %</th>
                <th className="px-4 py-3 text-right">Límite Diario</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="fintech-row" data-testid="agencia-skeleton-row">
                  <td colSpan={9} className="px-4 py-3"><Skeleton className="h-6 w-full"/></td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Sin agencias registradas.</td></tr>}
              {!isLoading && filtered.map((a) => (
                <tr key={a.id} className="fintech-row">
                  <td className="px-4 py-3 font-mono text-sky-600 dark:text-sky-300">{a.code}</td>
                  <td className="px-4 py-3 font-medium">{a.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{grupoMap[a.grupo_id]?.name || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.phone || "-"}</td>
                  <td className="px-4 py-3"><span className="text-xs font-semibold" data-testid={`agencia-currencies-${a.code}`}>{(a.currencies?.length ? a.currencies : [a.currency]).join(" / ")}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{a.commission_pct}%</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`agencia-limit-${a.code}`}>{fmtLimit(a.sales_limit_bs)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={!!a.active} onCheckedChange={() => setToggleTarget(a)} title={a.active ? "Activo — clic para inactivar" : "Inactivo — clic para activar"} data-testid={`agencia-toggle-${a.code}`}/>
                      <StatusBadge e={a} testid={`agencia-status-${a.code}`}/>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setConfigFor(a)} title="Cupos y comisiones" data-testid={`agencia-config-${a.code}`}><SlidersHorizontal size={16}/></Button>
                      <Button variant="ghost" size="icon" onClick={() => openClone(a)} title="Clonar" data-testid={`agencia-clone-${a.code}`}><Copy size={16}/></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`agencia-edit-${a.code}`}><Pencil size={16}/></Button>
                      <Button variant="outline" size="sm" className={`h-8 px-2.5 text-xs font-semibold ${a.suspended ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-500/10" : "border-amber-500/40 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10"}`} onClick={() => suspend.mutate({ id: a.id, suspended: !a.suspended })} disabled={suspend.isPending} data-testid={`agencia-suspend-${a.code}`}>
                        {a.suspended ? <><PlayCircle size={14} className="mr-1"/>Reanudar</> : <><PauseCircle size={14} className="mr-1"/>Suspender</>}
                      </Button>
                      <Button variant="ghost" size="icon" className="text-rose-300" onClick={() => { if (window.confirm(`Eliminar ${a.name}?`)) del.mutate(a.id); }} data-testid={`agencia-delete-${a.code}`}><Trash2 size={16}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal crear/editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar agencia" : "Nueva agencia"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="principales">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="principales" data-testid="agencia-tab-principales">Datos Principales</TabsTrigger>
              <TabsTrigger value="fiscales" data-testid="agencia-tab-fiscales">Datos Fiscales</TabsTrigger>
            </TabsList>
            <TabsContent value="principales" className="pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agencia-form-name"/></div>
                <div className="space-y-2"><Label>Código</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="agencia-form-code"/></div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Grupo (entidad superior)</Label>
                  <Select value={form.grupo_id} onValueChange={(v) => setForm({ ...form, grupo_id: v })}>
                    <SelectTrigger data-testid="agencia-form-grupo"><SelectValue placeholder="Selecciona un grupo"/></SelectTrigger>
                    <SelectContent>{grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} ({g.code})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Monedas habilitadas <span className="text-[11px] font-normal text-muted-foreground">(selección múltiple — solo las configuradas en el grupo)</span></Label>
                  <div className="flex gap-2" data-testid="agencia-form-currencies">
                    {(grupos.find((g) => g.id === form.grupo_id)?.currencies?.length ? grupos.find((g) => g.id === form.grupo_id).currencies : ["BS", "USD", "COP"]).map((c) => {
                      const on = form.currencies.includes(c);
                      return (
                        <button key={c} type="button" data-testid={`agencia-form-currency-${c}`}
                          onClick={() => {
                            const next = on ? form.currencies.filter((x) => x !== c) : [...form.currencies, c];
                            if (next.length === 0) { toast.error("Selecciona al menos una moneda"); return; }
                            setForm({ ...form, currencies: next, currency: next[0] });
                          }}
                          className={`rounded-lg border-2 px-4 py-2 text-sm font-bold transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                          {CURRENCY_LABELS[c] || c}
                        </button>
                      );
                    })}
                  </div>
                  {!form.grupo_id && <p className="text-[11px] text-muted-foreground">Selecciona primero un grupo para ver sus monedas disponibles.</p>}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="agencia-active"/>
                  <Label htmlFor="agencia-active">Activa</Label>
                </div>
                <div className="md:col-span-2 rounded-lg border-2 border-primary/30 bg-primary/5 p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-primary"/>
                    <Switch checked={form.enable_web_access} onCheckedChange={(v) => setForm({ ...form, enable_web_access: v })} id="agencia-web" data-testid="agencia-form-web-access"/>
                    <Label htmlFor="agencia-web" className="text-sm font-semibold">Web Access — usuario de solo lectura</Label>
                  </div>
                  {form.enable_web_access && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="agencia-web-access-fields">
                      <div className="space-y-1">
                        <Label className="text-xs">Usuario</Label>
                        <Input readOnly value={`age_${(form.code || "····").toLowerCase()}`} className="font-mono bg-secondary/60" data-testid="agencia-web-access-username"/>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Contraseña temporal</Label>
                        <Input readOnly value={WEB_ACCESS_TEMP_PASSWORD} className="font-mono bg-secondary/60 text-primary" data-testid="agencia-web-access-password"/>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="fiscales" className="pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>RIF / Cédula</Label><Input value={form.rif} onChange={(e) => setForm({ ...form, rif: e.target.value })} placeholder="J-000000000" data-testid="agencia-form-rif"/></div>
                <div className="space-y-2"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="agencia-form-phone"/></div>
                <div className="space-y-2 md:col-span-2"><Label>Dirección</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="agencia-form-address"/></div>
                <div className="space-y-2 md:col-span-2"><Label>Correo</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="opcional"/></div>
              </div>
            </TabsContent>
              {editing && (editing.suspended || !editing.active) && isBanca && (
                <div className="mt-3 rounded-lg border-2 border-rose-500/30 bg-rose-500/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="agencia-consult-window">
                  <div>
                    <div className="text-sm font-semibold text-rose-700 dark:text-rose-200 flex items-center gap-2"><Clock size={15}/> Ventana de consulta de arqueo (15 min)</div>
                    <div className="text-[11px] text-muted-foreground">Agencia {editing.active ? "suspendida" : "inactiva"}. Otorga acceso de SOLO consulta de cuadre. No habilita venta ni pago de tickets.</div>
                  </div>
                  {editing.temporary_consult_until && new Date(editing.temporary_consult_until) > new Date() ? (
                    <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1.5" data-testid="agencia-consult-active">Consulta activa</span>
                  ) : (
                    <Button type="button" variant="outline" className="border-rose-500/40 text-rose-700 dark:text-rose-200 hover:bg-rose-500/15" onClick={() => grantConsult.mutate(editing.id)} disabled={grantConsult.isPending} data-testid="agencia-grant-consult-button">Otorgar 15 min</Button>
                  )}
                </div>
              )}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => { if (!form.name?.trim() || !form.code?.trim()) { toast.error("Nombre y código son obligatorios"); return; } if (!form.grupo_id) { toast.error("Selecciona un grupo"); return; } save.mutate(buildPayload({ ...form, name: form.name.trim(), code: form.code.trim() })); }} disabled={save.isPending} data-testid="agencia-form-submit">{editing ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal clonar */}
      <Dialog open={!!cloneFor} onOpenChange={(v) => !v && setCloneFor(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Clonar agencia {cloneFor?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nombre de la nueva agencia</Label><Input value={cloneForm.target_name} onChange={(e) => setCloneForm({ ...cloneForm, target_name: e.target.value })} data-testid="agencia-clone-name"/></div>
            <div className="space-y-2"><Label>Código</Label><Input value={cloneForm.target_code} onChange={(e) => setCloneForm({ ...cloneForm, target_code: e.target.value.toUpperCase() })} data-testid="agencia-clone-code"/></div>
            <div className="space-y-2">
              <Label>Grupo destino</Label>
              <Select value={cloneForm.grupo_id} onValueChange={(v) => setCloneForm({ ...cloneForm, grupo_id: v })}>
                <SelectTrigger data-testid="agencia-clone-grupo"><SelectValue/></SelectTrigger>
                <SelectContent>{grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} ({g.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Se copian comisión ({cloneFor?.commission_pct}%), moneda, dirección y teléfono.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneFor(null)}>Cancelar</Button>
            <Button onClick={() => clone.mutate({ id: cloneFor.id, payload: cloneForm })} disabled={clone.isPending || !cloneForm.target_code} data-testid="agencia-clone-submit">Clonar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirmar habilitar/deshabilitar */}
      <Dialog open={!!toggleTarget} onOpenChange={(v) => !v && setToggleTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="agencia-toggle-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Power size={18}/> {toggleTarget?.active ? "Inactivar" : "Activar"} agencia</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            ¿Confirmas {toggleTarget?.active ? "marcar como INACTIVA (baja permanente)" : "activar"} la agencia <span className="font-semibold text-foreground">{toggleTarget?.name}</span>? Esta acción quedará registrada en auditoría.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleTarget(null)}>Cancelar</Button>
            <Button onClick={() => toggleStatus.mutate(toggleTarget.id)} disabled={toggleStatus.isPending} data-testid="agencia-toggle-confirm">{toggleTarget?.active ? "Inactivar" : "Activar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {configFor && <EntityConfigDialog open={!!configFor} onClose={() => setConfigFor(null)} entityType="agencia" entity={configFor}/>}
    </div>
  );
}
